import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import invariant from "tiny-invariant";
import type { Positions } from "./useCanvasToolsPositions";

export interface CanvasToolsPositionsContextValue {
  blockActionsRect: DOMRect | null;
  blockActionsRef: (node: HTMLDivElement | null) => void;
  positions: Positions;
}

const CanvasToolsPositionsContext = createContext<
  CanvasToolsPositionsContextValue | undefined
>(undefined);
CanvasToolsPositionsContext.displayName = "CanvasToolsPositionsContext";

export interface CanvasToolsPositionsProviderProps {
  children: ReactNode;
  positions: Positions;
}

export const CanvasToolsPositionsProvider = ({
  children,
  positions,
}: CanvasToolsPositionsProviderProps) => {
  const [blockActionsRect, setBlockActionsRect] = useState<DOMRect | null>(
    null,
  );

  const blockActionsRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const rect = node.getBoundingClientRect();
      // Create a clean copy of the rect data to avoid DOM references
      setBlockActionsRect({
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
      } as DOMRect);
    } else {
      setBlockActionsRect(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      blockActionsRef,
      positions,
      blockActionsRect,
    }),
    [positions, blockActionsRect, blockActionsRef],
  );

  return (
    <CanvasToolsPositionsContext.Provider value={value}>
      {children}
    </CanvasToolsPositionsContext.Provider>
  );
};

export const useCanvasToolsPositionsContext =
  (): CanvasToolsPositionsContextValue => {
    const context = useContext(CanvasToolsPositionsContext);
    invariant(
      context,
      "useCanvasToolsPositionsContext must be used within a CanvasToolsPositionsProvider",
    );
    return context;
  };
